/**
 * Google Analytics (GA4) tracking module for the superhero.pw app.
 *
 * Uses the same measurement ID (G-4E584T52JW) as the marketing site
 * to unify analytics across both properties.
 *
 * Events are only sent in production (when VITE_ENV=production or
 * the API host is detected as a remote domain).
 *
 * Usage:
 *   import { analytics, AnalyticsEvents } from '../lib/analytics';
 *
 *   // Page views are tracked automatically via the AnalyticsProvider.
 *   // For custom events:
 *   analytics.track(AnalyticsEvents.PAGE_CREATED, { pageId: '...' });
 */

import { ENVIRONMENT_CONFIG } from './config';

// ─── GA4 Measurement ID ─────────────────────────────────────────────────
// This is the same measurement ID used on the marketing site (superhero.pw)
// to unify analytics across the marketing site and app.
const GA_MEASUREMENT_ID = 'G-4E584T52JW';

// ─── gtag global type ───────────────────────────────────────────────────
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

// ─── Initialize GTAG ──────────────────────────────────────────────────────
let initialized = false;

function initGtag(): boolean {
  if (initialized) return true;
  if (typeof window === 'undefined') return false;

  // Only initialize in production
  if (!ENVIRONMENT_CONFIG.isProduction) {
    // eslint-disable-next-line no-console
    console.log('[analytics] GA4 disabled in development — events will be logged to console.');
    initialized = true;
    return false;
  }

  // Inject GA4 script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Initialize dataLayer and gtag
  window.dataLayer = window.dataLayer || [];
  window.gtag = function (...args: any[]) {
    window.dataLayer.push(args);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false, // We handle page views manually via the provider
  });

  initialized = true;
  return true;
}

// ─── Standard Event Names ─────────────────────────────────────────────────
export const AnalyticsEvents = {
  // Authentication
  USER_SIGNED_UP: 'sign_up',
  USER_SIGNED_IN: 'login',
  USER_SIGNED_OUT: 'logout',

  // Page / Content
  PAGE_CREATED: 'page_created',
  PAGE_UPDATED: 'page_updated',
  PAGE_DELETED: 'page_deleted',
  PAGE_VIEWED: 'page_viewed',
  PAGE_EXPORTED: 'page_exported',
  PAGE_IMPORTED: 'page_imported',

  // Project / Serial
  PROJECT_CREATED: 'project_created',
  PROJECT_UPDATED: 'project_updated',
  PROJECT_DELETED: 'project_deleted',
  SERIAL_CREATED: 'serial_created',

  // Character
  CHARACTER_CREATED: 'character_created',
  CHARACTER_UPDATED: 'character_updated',
  CHARACTER_DELETED: 'character_deleted',

  // Task / Production
  TASK_CREATED: 'task_created',
  TASK_COMPLETED: 'task_completed',
  WEEK_WORK_CREATED: 'week_work_created',
  STAGE_TOGGLED: 'stage_toggled',

  // Reference / Attachment
  REFERENCE_ADDED: 'reference_added',
  REFERENCE_DELETED: 'reference_deleted',
  ATTACHMENT_UPLOADED: 'attachment_uploaded',

  // Notes / Comments
  NOTE_CREATED: 'note_created',
  COMMENT_CREATED: 'comment_created',

  // Editor
  EDITOR_MODE_CHANGED: 'editor_mode_changed',
  SIDE_PANEL_OPENED: 'side_panel_opened',
  LAYOUT_CHANGED: 'layout_changed',

  // Palette
  PALETTE_CREATED: 'palette_created',
  PALETTE_APPLIED: 'palette_applied',

  // Navigation
  PAGE_NAVIGATE: 'page_view', // GA4 standard page_view event

  // Billing
  PLAN_SELECTED: 'plan_selected',
  CHECKOUT_STARTED: 'begin_checkout',
} as const;

export type AnalyticsEventName = typeof AnalyticsEvents[keyof typeof AnalyticsEvents];

// ─── Analytics Class ──────────────────────────────────────────────────────

class Analytics {
  private enabled: boolean;

  constructor() {
    this.enabled = initGtag();
  }

  /**
   * Track a custom event with optional parameters.
   * In development, logs to console instead.
   */
  track(eventName: string, params?: Record<string, any>): void {
    if (!this.enabled) {
      // Development: log to console for debugging
      // eslint-disable-next-line no-console
      console.log(`[analytics] ${eventName}`, params);
      return;
    }

    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, {
          ...params,
          app_source: 'superhero-app',
        });
      }
    } catch (err) {
      // Silently fail — analytics should never break the app
    }
  }

  /**
   * Track a page view. Call this on route changes.
   */
  pageView(path: string, title?: string): void {
    if (!this.enabled) {
      // eslint-disable-next-line no-console
      console.log(`[analytics] page_view: ${path}`, title);
      return;
    }

    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'page_view', {
          page_path: path,
          page_title: title || document.title,
          app_source: 'superhero-app',
        });
      }
    } catch (err) {
      // Silently fail
    }
  }

  /**
   * Set user identity for cross-session tracking.
   */
  setUserId(userId: string): void {
    if (!this.enabled) return;

    try {
      if (typeof window.gtag === 'function') {
        window.gtag('config', GA_MEASUREMENT_ID, {
          user_id: userId,
        });
      }
    } catch (err) {
      // Silently fail
    }
  }

  /**
   * Set a user property (e.g., subscription tier).
   */
  setUserProperty(name: string, value: string): void {
    if (!this.enabled) return;

    try {
      if (typeof window.gtag === 'function') {
        window.gtag('set', 'user_properties', {
          [name]: value,
        });
      }
    } catch (err) {
      // Silently fail
    }
  }
}

// ─── Singleton ─────────────────────────────────────────────────────────────
export const analytics = new Analytics();

// ─── Re-export config reference ───────────────────────────────────────────
export { GA_MEASUREMENT_ID };